import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { verifyPayment } from '../services/paymentService';
import './Payment.css';

const Payment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Hide the site navbar when opened as a standalone checkout from the embedded
  // reservation widget (the widget appends ?embed=1 when breaking out to the top window).
  const hideChrome = searchParams.get('embed') === '1';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);

  useEffect(() => {
    const initPayment = async () => {
      // Get payment URL and reservation ID from query params
      const url = searchParams.get('url');
      const resId = searchParams.get('reservationId');
      const restName = searchParams.get('restaurantName');

      if (!url || !resId) {
        setError('Invalid payment session. Please try booking again.');
        setLoading(false);
        return;
      }

      // Check if payment is already complete before sending the user to Paymob
      try {
        const paymentStatus = await verifyPayment(resId);
        if (paymentStatus.paymentStatus === 'paid') {
          setRestaurantName(restName || 'Restaurant');
          setIsAlreadyPaid(true);
          setLoading(false);
          return;
        }
      } catch (err) {
        // If verification fails, continue to the checkout (let Paymob handle it)
        console.log('Payment verification check failed, proceeding to checkout:', err);
      }

      // Full-page redirect to the Paymob-hosted checkout (Case 1): the browser leaves
      // our site so Apple Pay runs on Paymob's verified domain. After payment Paymob
      // redirects the browser back to /payment-success via the API callback.
      window.location.href = decodeURIComponent(url);
    };

    initPayment();
  }, [searchParams]);

  if (error) {
    return (
      <>
        {!hideChrome && <Navbar />}
        <div className={`payment-page-container${hideChrome ? " payment-embed" : ""}`}>
          <div className="payment-error-card">
            <div className="error-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>
            <h2>Payment Error</h2>
            <p>{error}</p>
            <button className="btn-back" onClick={() => navigate('/')}>
              <i className="bi bi-arrow-left me-2"></i>
              Back to Home
            </button>
          </div>
        </div>
      </>
    );
  }

  if (isAlreadyPaid) {
    return (
      <>
        {!hideChrome && <Navbar />}
        <div className={`payment-page-container${hideChrome ? " payment-embed" : ""}`}>
          <div className="payment-success-card">
            <div className="success-icon">
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <h2>Already Paid</h2>
            <p>
              This reservation at <strong>{restaurantName}</strong> has already been paid.
            </p>
            <button className="btn-back" onClick={() => navigate('/my-reservations')}>
              <i className="bi bi-calendar-check me-2"></i>
              View My Reservations
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!hideChrome && <Navbar />}
      <div className={`payment-page-container${hideChrome ? " payment-embed" : ""}`}>
        <div className="payment-iframe-container">
          <div className="payment-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>{loading ? 'Loading payment…' : 'Redirecting to secure payment…'}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Payment;
